import React, { useState, useEffect, useMemo } from 'react';
import { 
  LuTrash2, LuMail, LuPhone, LuUser, LuGlobe, LuFlag, LuFileEdit, 
  LuSearch, LuDownload, LuFilter, LuCircleCheck, LuX, LuCalendar, LuClock, LuHistory
} from 'react-icons/lu';
import axios from 'axios';
import { CSVLink } from "react-csv";
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const WebinarManagement = ({ onCountChange }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRegistrations(response.data.data);
        if (typeof onCountChange === 'function') {
          onCountChange(response.data.data.length);
        }
      }
    } catch (error) {
      setError('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRegistrations();
    } catch (error) {
      setError('Failed to delete registration');
    }
  };

  const filteredRegistrations = useMemo(() => {
    return registrations.filter(reg => {
      const q = searchQuery.toLowerCase();
      return !searchQuery ||
        reg.name?.toLowerCase().includes(q) ||
        reg.email?.toLowerCase().includes(q) ||
        reg.mobile?.toLowerCase().includes(q) ||
        reg.usTaxStatus?.toLowerCase().includes(q);
    });
  }, [registrations, searchQuery]);

  const csvData = useMemo(() => {
    return filteredRegistrations.map(reg => ({
      'Name': reg.name,
      'Email': reg.email,
      'Phone': `${reg.countryCode} ${reg.mobile}`,
      'Tax Status': reg.usTaxStatus,
      'Location': reg.currentlyLiving,
      'Submitted At': moment(reg.createdAt).format('YYYY-MM-DD HH:mm:ss')
    }));
  }, [filteredRegistrations]);

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={5} />;
  }

  return (
    <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: registrations.length, icon: LuUser, iconBg: 'bg-primary-100 dark:bg-primary-900/30', iconColor: 'text-primary-600 dark:text-primary-400' },
            { label: 'US Citizens', value: registrations.filter(r => r.usTaxStatus === 'US Citizen').length, icon: LuFlag, iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
            { label: 'From USA', value: registrations.filter(r => r.currentlyLiving === 'USA').length, icon: LuGlobe, iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
            { label: 'Filtered', value: filteredRegistrations.length, icon: LuHistory, iconBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-600 dark:text-purple-400' },
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-card">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search name, email, mobile..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setSelectionMode(!selectionMode)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${selectionMode ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"}`}
              >
                <LuCircleCheck />
                {selectionMode ? "Active" : "Select"}
              </button>
              <CSVLink
                data={csvData}
                filename={`Webinar_Registrations_${moment().format('YYYY-MM-DD')}.csv`}
                className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap"
              >
                <LuDownload size={18} />
                Export CSV
              </CSVLink>
            </div>
          </div>
        </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left text-sm font-semibold">Attendee</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Phone</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Tax Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Location</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
              {filteredRegistrations.map((reg) => (
                <tr key={reg._id} className="hover:bg-primary-50/20 dark:hover:bg-primary-900/10 cursor-pointer" onClick={() => setSelectedRegistration(reg)}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/30 dark:to-primary-900/10 flex items-center justify-center flex-shrink-0 border border-primary-200/50"><LuUser className="text-primary-600 dark:text-primary-400 w-3.5 h-3.5" /></div>
                      <span className="font-bold text-gray-900 dark:text-white text-sm">{reg.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs font-bold text-primary-600">{reg.email}</td>
                  <td className="px-6 py-4 text-xs font-bold text-gray-700 dark:text-gray-300">{reg.countryCode} {reg.mobile}</td>
                  <td className="px-6 py-4"><span className="px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase">{reg.usTaxStatus}</span></td>
                  <td className="px-6 py-4 text-xs font-bold text-gray-500">{reg.currentlyLiving}</td>
                  <td className="px-6 py-4 text-xs font-bold text-gray-500">{moment(reg.createdAt).format('MMM DD, YYYY')}</td>
                  <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-dark-800 shadow-left group-hover:bg-primary-50/20"><button className="px-3 py-1.5 text-[10px] font-black uppercase bg-gray-100 rounded-lg">View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedRegistration(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
             <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold">Registration Details</h2>
              <button onClick={() => setSelectedRegistration(null)} className="p-2 hover:bg-gray-100 rounded-xl"><LuX /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Name</label><p className="font-bold">{selectedRegistration.name}</p></div>
                <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Tax Status</label><p className="font-bold">{selectedRegistration.usTaxStatus}</p></div>
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Questions</label><p className="font-medium italic leading-relaxed text-sm">"{selectedRegistration.questions || 'No questions provided.'}"</p></div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setSelectedRegistration(null)} className="px-6 py-2 bg-gray-100 rounded-xl font-bold text-xs uppercase">Close</button>
              <button onClick={() => { handleDelete(selectedRegistration._id); setSelectedRegistration(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold text-xs uppercase">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebinarManagement;
